import 'react-app-polyfill/ie9';
import 'react-app-polyfill/ie11';
import 'core-js';

import React from 'react';
// React 18: createRoot は 'react-dom/client' からインポートする（'react-dom' 直下は非対応）
import { createRoot } from 'react-dom/client';
// デザインシステムのトークン（CSS 変数）を最初に読み込む。
// index.css より前に置くことで :root のカスタムプロパティが先に定義される。
import './styles/tokens.css';
import './index.css';
import App from './components/App';
import * as serviceWorker from './serviceWorker';
import { BrowserRouter } from "react-router-dom";

// antd 全体テーマ（デザインシステム Ph.1）
import { ConfigProvider } from 'antd';
import jaJP from 'antd/locale/ja_JP';

// Axios設定をインポート（全てのAPIリクエストにAPIキーを追加）
import './utils/axiosConfig';

import Reducer from './_reducers';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import promiseMiddleware from 'redux-promise';
import ReduxThunk from 'redux-thunk';

// ─────────────────────────────────────────────────────────────
// デザインシステム theme トークン（antd 5.29 cssVar モード）
//   - cssVar: true … antd が独自の CSS 変数を生成（新デザインの tokens.css と整合）
//   - token 値は HEX/px のリテラルが必須（antd が派生色を算出するため var() 不可）。
//     値は src/styles/tokens.css のブランド色・サイズと一致させている（単一の出典）。
//   - 値の変更時は .claude/rules/design-system.md と tokens.css も同期すること。
// ─────────────────────────────────────────────────────────────
const designSystemTheme = {
  cssVar: true,
  token: {
    // --- カラー（ブランド／テーマ） ---
    colorPrimary: '#FF6B35', // brand-primary（Orange）─ 主 CTA
    colorInfo:    '#0EA5E9', // brand-blue ─ 情報・通知
    // リンク色: brand-blue(#0EA5E9)は白背景で 2.77:1 と AA 未達のため、
    // 本文リンクには濃い blue-active(#0369A1, 5.91:1)を使う（WCAG 2.2 AA / Ph.2）。
    colorLink:    '#0369A1', // blue-active ─ リンク（コントラスト確保）
    colorSuccess: '#2E7D4B', // green-active ─ 成功
    colorError:   '#D44A18', // red 系（orange-active 相当の濃い色）─ エラー
    colorWarning: '#E8A30A', // yellow-default ─ 警告（UI 限定）
    // 背景: ページ（レイアウト）は Cream、コンテナ（カード・モーダル）は白。
    // Ph.4 で Cream を全面適用。カードは白を保ち Cream の上に「浮いて」見せる。
    // colorBgLayout は antd <Layout> に効く。本アプリは Layout 使用箇所が少ないため、
    // ページ全体の Cream 背景は index.css の body 背景（--brand-cream）が主に担う。
    colorBgLayout:    '#FCF8EE', // = --brand-cream（ページ背景）
    colorBgContainer: '#FFFFFF', // カード・モーダル・入力欄は白を維持
    // オーバーレイの背景暗転（mask）。ショーケース §26 は cool rgba(15,19,24,0.42) だが、
    // rcdrs は warm パレット（--black #23221F）へ統一済みのため warm black @ 0.42 で同期。
    // mask を持つオーバーレイ（Modal・Drawer・Image preview）にのみ効く（Popover/Tooltip は無関係）。
    colorBgMask:      'rgba(35, 34, 31, 0.42)', // = --black @ 42%（antd 既定 rgba(0,0,0,0.45) から変更）

    // --- 角丸 ---
    borderRadius:   6,  // = --radius-md（中型ボタン・バッジ）
    borderRadiusLG: 10, // = --radius-xl（カード・モーダル・ドロワー）

    // --- タイポグラフィ ---
    fontFamily: '"Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic", "Meiryo", "Noto Sans JP", sans-serif',
    fontSize:   15, // = --text-base（antd 既定 14 から変更）

    // --- サイズ（コントロール高さ） ---
    controlHeight: 40, // ボタン・入力欄の高さ（antd 既定 32 から変更。WCAG タッチ領域に配慮）

    // --- 影・モーション ---
    boxShadow:         '0 1px 3px rgba(0, 0, 0, 0.04)', // = --shadow-card
    motionDurationMid: '200ms', // = --duration-base
  },
  // ─────────────────────────────────────────────────────────────
  // コンポーネント別オーバーライド（antd 5.29 theme.components）
  //   ※ token 値は HEX リテラルが必須（antd が派生色を算出するため var() 不可）。
  //     値は src/styles/tokens.css と一致させる（単一の出典）。
  // ─────────────────────────────────────────────────────────────
  components: {
    // テーブル（ショーケース §16「テーブル」整合 / Ph.6 5 次 → 2 次トラック S3-b で再方向）
    //   - ヘッダー背景: 2 次トラック S3-b（企画 item 4.4/18.4 + コントロールセンター option A）で
    //     §16 の最新方向「cream 塗り + 上下線」へ全テーブル昇格。--grey-10 塗り → --brand-cream
    //     （= ページ背景と同色＝「塗らない」効果）。ヘッダー帯の上下線は index.css のグローバル規則が担う。
    //   - ソート hover/active 背景は --grey-20（cream より一段濃い warm grey）＝ cream ヘッダー上でも
    //     自然なフィードバック濃度のため据え置き。
    //   - 行 hover: 既定の中性 grey → ブランド primary の最淡 tint（§16 の primary-50 hover wash）
    //   全て tokens.css の既存 warm トークンで表現可能（hue 衝突なし）。
    Table: {
      headerBg:           '#FCF8EE', // = --brand-cream（ヘッダー背景。S3-b で --grey-10 から変更）
      headerColor:        '#23221F', // = --black（ヘッダーテキスト）
      headerSortActiveBg: '#EFE9D7', // = --grey-20（ソート active ヘッダー）
      headerSortHoverBg:  '#EFE9D7', // = --grey-20（ソート可能ヘッダー hover）
      rowHoverBg:         '#FFEFE7', // = --orange-background（行 hover ＝ §16 の「온마우스 반전」色）
      cellHoverBg:        '#FFEFE7', // = --orange-background（ソート列セル hover も行 hover と統一）
      borderColor:        '#EFE9D7', // = --grey-20（罫線を warm grey へ。既定 cool #f0f0f0 から）
    },
    // ナビゲーション（ショーケース §17「ナビゲーション」整合 / Ph.6 6 次）
    //   - 非選択リンク: --grey-65、hover で --black 文字 + 크림 wash（--grey-5）＝ §17 の「온마우스 반전」色
    //   - 選択中: --orange-active 文字 + --orange-background wash（§17 .demo-topnav-link.is-active）
    //   - active 下線（ink bar）は §17 が背景表現のため抑制（activeBarHeight: 0）
    //   ※ <Menu> 使用は NavBar（LeftMenu/RightMenu）のみ。他画面に影響なし（Grep 確認）。
    //   §17 の色は全て tokens.css の既存 warm トークンで表現可能（hue 衝突なし＝そのまま흡수）。
    Menu: {
      itemColor:                   '#6B6760', // = --grey-65（非選択リンク文字）
      itemHoverColor:              '#23221F', // = --black（hover 文字）
      itemHoverBg:                 '#FCF8EE', // = --grey-5 / cream（hover wash）
      itemSelectedColor:           '#D44A18', // = --orange-active（選択文字）
      itemSelectedBg:              '#FFEFE7', // = --orange-background（選択 wash）
      horizontalItemHoverColor:    '#23221F', // = --black
      horizontalItemHoverBg:       '#FCF8EE', // = --grey-5 / cream
      horizontalItemSelectedColor: '#D44A18', // = --orange-active
      horizontalItemSelectedBg:    '#FFEFE7', // = --orange-background
      horizontalItemBorderRadius:  0,         // §17 はバー高いっぱいの塗り（角丸なし）
      activeBarHeight:             0,         // §17 は背景表現のため下線（ink bar）を抑制
    },
    // 入力欄の選択デザイン（企画 デザイン修正依頼1 item 3 / 2 次トラック S4 → 修正依頼2 item② / 3 次トラック T3）
    //   - item 3.2 → 修正依頼2 item②: プルダウン選択肢に hover した時の背景を cream（--brand-cream #FCF8EE）へ。
    //     ★S4 では cream がページ背景・白ドロップダウンとほぼ同色で不可視のため --grey-10（#F2EBD5）を採用したが、
    //     企画が修正依頼2 item② で改めて cream（#FCF8EE）を明示再要求（S4 判断の역전）。企画ポリシー最優先で cream へ戻した。
    //     ライブ実測で白ドロップダウン上での視認性は許容範囲（意図された옅은 warm hover）。
    //   - item 3.3 の一部: 選択済みオプションの背景を、antd が colorPrimary（橙）から派生させる
    //     淡い橙 tint から、明示トークン --orange-background（#FFEFE7）へ固定。§16 テーブル行
    //     hover・選択ハイライトと同じ warm「選択/活性」色で統一（hover=cream / 選択=ブランド warm の階層）。
    //   - optionSelectedColor は据え置き（--black 既定。warm 淡背景上で AA 十分）。
    //   ※ selector の二重枠→単一線・フォーカス時の淡い背景は index.css 側（フォーカスルーティング）で扱う。
    Select: {
      optionActiveBg:    '#FCF8EE', // = --brand-cream（hover した選択肢の背景。修正依頼2 item② で grey-10 → cream へ역전）
      optionSelectedBg:  '#FFEFE7', // = --orange-background（選択済みオプションの淡い warm 背景）
    },
    // タブ（ショーケース §14「タブ」整合 / 企画 デザイン修正依頼1 item 7 / 2 次トラック S7）
    //   - ショーケースの「タブ」はライン型（下線 ink bar）。実画面（PDF30010/70010/80010）は
    //     従来 type="card" だったが、image 16（＝ショーケース）に合わせライン型へ統一（card→line・ユーザー決定）。
    //   - 非選択: --grey-65 文字、hover で --black 文字、選択中: --orange-active 文字 + --orange-default の ink bar。
    //   ※ 色は全て tokens.css の既存 warm トークン（Orange + grey）で表現可能（hue 衝突なし＝そのまま흡수）。
    //   ※ hover の cream 背景 wash と font-weight 700 はショーケース副次仕様。antd Tabs に該当トークンが無く
    //     .ant-* 上書きが要るため、core（色 + ink bar = image 16 の主表現）を theme で達成し未適用とする
    //     （Ph.6 5 次 Table のタイポ変形を見送った先例と同方針＝色が中核）。
    Tabs: {
      itemColor:         '#6B6760', // = --grey-65（非選択タブ文字。§14 .tab の color）
      itemHoverColor:    '#23221F', // = --black（hover 文字。§14 .tab:hover の color）
      itemActiveColor:   '#D44A18', // = --orange-active（押下時の文字）
      itemSelectedColor: '#D44A18', // = --orange-active（選択中の文字。§14 .tab.is-active の color）
      inkBarColor:       '#FF6B35', // = --orange-default（選択中の下線 ink bar。§14 .tab.is-active の border-bottom-color）
    },
  },
};

const createStoreWithMiddleware = applyMiddleware(promiseMiddleware, ReduxThunk)(createStore);

// React 18: createRoot による新しいルートAPIへ移行（旧 ReactDOM.render から）
const root = createRoot(document.getElementById('root'));
root.render(
    // StrictMode: 開発時の二重レンダリングで副作用を表面化させる（本番ビルドには影響なし）
    <React.StrictMode>
        <Provider
            store={createStoreWithMiddleware(
                Reducer,
                window.__REDUX_DEVTOOLS_EXTENSION__ &&
                window.__REDUX_DEVTOOLS_EXTENSION__()
            )}
        >
            <BrowserRouter>
                {/* ConfigProvider: アプリ全体に antd テーマ（デザインシステム）と日本語ロケールを適用。
                    各画面に残る入れ子の ConfigProvider（DatePicker 用 locale）は本設定にマージされる。 */}
                <ConfigProvider theme={designSystemTheme} locale={jaJP}>
                    <App />
                </ConfigProvider>
            </BrowserRouter>
        </Provider>
    </React.StrictMode>
);
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
